import type { ReactElement } from 'react';
import { Flex, Radio, Spinner } from '@chakra-ui/react';
import { FormattedMessage } from 'react-intl';
import { Text } from '../../../../../ui-components';
import { isDropship, isGiftRegistry } from '../../../../../utils/product-utils';
import type { ProductItem, ShippingOption } from '../../../../../bff-api/baskets/model';
import type { ProductInventory } from '../../../../../bff-api/baskets/model/BasketInventory';
import type { Store } from '../../../../../bff-api/stores/model';

interface Props {
    id: string;
    productItem: ProductItem;
    option?: ShippingOption;
    inventory?: ProductInventory;
    bopisStore?: Store | null;
    onOpenStoreSearch: () => void;
    isLoading: boolean;
}

export const CartBopisOption = ({
    id,
    productItem,
    option,
    inventory,
    bopisStore,
    onOpenStoreSearch,
    isLoading,
}: Props): ReactElement | null => {
    if (!option || !inventory) {
        return null;
    }

    if (
        !option.enabled ||
        !productItem.c_online ||
        isDropship(productItem) ||
        isGiftRegistry(productItem)
    ) {
        return (
            <Flex data-testid={`${id}-not-available`} gap={2} alignItems="start" opacity={0.5}>
                <Radio data-testid={`${id}-radio`} value={option.type} width={4} isDisabled />

                <Flex direction="column">
                    <Text id={`${id}-label`} style="bold">
                        <FormattedMessage
                            id="delivery.options.store.pickup"
                            defaultMessage="Store Pickup"
                        />
                    </Text>

                    <Text id={`${id}-not-available-label`} size="small">
                        <FormattedMessage id="not.available" defaultMessage="Not Available" />
                    </Text>
                </Flex>
            </Flex>
        );
    }

    if (!bopisStore) {
        return (
            <Flex data-testid={`${id}-store-not-available`} gap={2} alignItems="start">
                <Radio data-testid={`${id}-radio`} value={option.type} width={4} isDisabled />

                <Flex direction="column">
                    <Text id={`${id}-label`} style="bold">
                        <FormattedMessage
                            id="delivery.options.store.pickup"
                            defaultMessage="Store Pickup"
                        />
                    </Text>

                    <Text
                        id={`${id}-choose-location`}
                        size="small"
                        style="bold"
                        type="link"
                        onClick={onOpenStoreSearch}
                    >
                        <FormattedMessage
                            id="store.pick.up.choose.location"
                            defaultMessage="Choose Location"
                        />
                    </Text>
                </Flex>
            </Flex>
        );
    }

    const hasEnoughQuantity = inventory?.bopisInventory?.availableToSell >= productItem.quantity;

    return isLoading || (bopisStore.c_storePickUpEnableFlag && hasEnoughQuantity) ? (
        <Flex data-testid={`${id}-available`} gap={2} alignItems="start">
            {isLoading ? (
                <Spinner data-testid={`${id}-spinner`} size="sm" marginTop={1} />
            ) : (
                <Radio data-testid={`${id}-radio`} value={option.type} width={4} />
            )}

            <Flex direction="column">
                <Text id={`${id}-label`} style="bold">
                    <FormattedMessage
                        id="delivery.options.store.pickup"
                        defaultMessage="Store Pickup"
                    />
                </Text>

                <Text
                    id={`${id}-store-name`}
                    size="small"
                    style="bold"
                    type="link"
                    onClick={onOpenStoreSearch}
                >
                    {bopisStore?.name}
                </Text>
            </Flex>
        </Flex>
    ) : (
        <Flex data-testid={`${id}-not-available-in-store`} gap={2} alignItems="start">
            <Radio data-testid={`${id}-radio`} value={option.type} width={4} isDisabled />

            <Flex direction="column">
                <Text id={`${id}-label`} style="bold">
                    <FormattedMessage
                        id="delivery.options.store.pickup"
                        defaultMessage="Store Pickup"
                    />
                </Text>

                <Text id={`${id}-not-available.in`}>
                    <FormattedMessage id="not.available.in" defaultMessage="Not available in" />
                </Text>

                <Text
                    id={`${id}-store-name`}
                    size="small"
                    style="bold"
                    type="link"
                    onClick={onOpenStoreSearch}
                >
                    {bopisStore?.name}
                </Text>
            </Flex>
        </Flex>
    );
};

CartBopisOption.defaultProps = {
    option: undefined,
    inventory: undefined,
    bopisStore: undefined,
};
